import React from 'react';
import { faKey, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import apiService from "../../services/apiKeyService";

type ApiKey = {
  id: number;
  name: string;
  condensedName: string;
  apiKeyType: string;
};

export type Organization = {
  id: number;
  apiKeys: ApiKey[];
  organizationTypeId: number;
};

type ApiKeyButtonProps = {
  showModal: Function;
  closeModal: Function;
  organization: Organization;
  onSaved: Function;
};

export const OrgTypes = {
  Organization: 1,
  ParentOrganization: 2,
  ChildOrganization: 3,
  Steris: 4,
} as const;

export const ApiKeyUserRolesEnum = {
  //🧙‍♂️ keys from the database
  CasechekApiClient: {
    id: 30200,
    name: "Casechek Api Client",
    condensedName: "casechekApiClient",
  },
  TorqApiClient: {
    id: 30300,
    name: "Torq Api Client",
    condensedName: "torqApiClient",
  },
  Ls360ApiClient: {
    id: 30301,
    name: "LS360 Api Client",
    condensedName: "ls360ApiClient",
  },
  SpmApiClient: {
    id: 30000,
    name: "Spm Api Client",
    condensedName: "spmApiClient",
  },
  DynamicConfigApiClient: {
    id: 30100,
    name: "Dynamic Config Api Client",
    condensedName: "dynamicConfigApiClient",
  },
  SynergyTrakApiClient: {
    id: 31000,
    name: "SynergyTrak Api Client",
    condensedName: "synergyTrakApiClient",
  },
  AcmewareApiClient: {
    id: 30400,
    name: "Acmeware Api Client",
    condensedName: "acmewareApiClient",
  },
} as const;

type ApiKeyUserRole =
  (typeof ApiKeyUserRolesEnum)[keyof typeof ApiKeyUserRolesEnum];

const ls360ApiKeyRoles: ApiKeyUserRole[] = [
  ApiKeyUserRolesEnum.TorqApiClient,
  ApiKeyUserRolesEnum.Ls360ApiClient,
];

export type ApiKeyTypeOption = {
  id: number;
  name: string;
  displayName: string;
  condensedName: string;
};

// `name` is the role name shared with the server and must keep matching the
// database. Anything shown to a user goes through getApiKeyDisplayName instead.
const apiKeyDisplayNames: Record<string, string> = {
  [ApiKeyUserRolesEnum.Ls360ApiClient.name.toUpperCase()]:
    "DeepThink Api Client",
};

export const getApiKeyDisplayName = (apiKeyType: string) =>
  (apiKeyType && apiKeyDisplayNames[apiKeyType.toUpperCase()]) || apiKeyType;

const orgHasApiKey = (organization: Organization, apiKeyName: string) =>
  organization.apiKeys.some(
    (existingKey) =>
      existingKey.apiKeyType.toUpperCase() === apiKeyName.toUpperCase()
  );

export const isLs360ApiKeyType = (apiKeyType: string) =>
  ls360ApiKeyRoles.some(
    (role) => role.name.toUpperCase() === apiKeyType.toUpperCase()
  );

// Torq and LS360 can both be added to the same org. Only one of them is ever
// enabled, so they are offered alongside every other type until the org has both.
export const getOrgKeyTypesToShow = (
  organization: Organization
): ApiKeyTypeOption[] => {
  const orgType = organization.organizationTypeId;
  if (orgType === OrgTypes.ParentOrganization || !organization.apiKeys) {
    return [];
  }

  return Object.values(ApiKeyUserRolesEnum)
    .filter((role) => !orgHasApiKey(organization, role.name))
    .map(({ id, name, condensedName }) => ({
      id,
      name,
      displayName: getApiKeyDisplayName(name),
      condensedName,
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
};

export const ApiKeyButton = ({
  showModal,
  closeModal,
  organization,
  onSaved,

}: ApiKeyButtonProps) => {
  const apiKeyTypes = getOrgKeyTypesToShow(organization);
  const { t } = useTranslation();
  const handleCreateApiKey = async (keyTypeToAdd: number) => {
    var apiKey = {
      organizationId: organization.id,
      userRole: keyTypeToAdd,
    };

    const response = await apiService.createApiKey(apiKey);
    if (response.data.success) {
      var result = response.data.payload;
      onSaved(result);
      closeModal();
    } else if (response.data.message) {
      window.alert(response.data.message);
      closeModal();
    }
  };

  return (
    <span
      className="fa-layers fa-fw text-success float-right show-hand"
      onClick={() => {
        showModal(
          {
            open: true,
            focus: true,
            title: t("Create New API Key"),
            message: t(
              "Please select the API key type you wish to create for this customer."
            ),
            model: apiKeyTypes,
            closeModal: closeModal,
            saveModel: handleCreateApiKey,
          },
          "createApiKeyModal"
        );
      }}

    >
      <FontAwesomeIcon icon={faKey} transform={{ rotate: -45 }} />
      <FontAwesomeIcon icon={faPlus} transform="shrink-8 right-10" />
    </span>
  );
};


